'use strict';
import { map, filter, flatMap, takeUntil, take, distinctUntilChanged, tap, debounceTime, mergeMap, publish } from 'rxjs/operators';
import {
  OnInit,
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  Input, SimpleChanges, OnChanges, ViewChild, ElementRef, ViewRef
} from '@angular/core';
import { ConnectableObservable, Observable, Subject, Subscription } from 'rxjs';
import { get, isEmpty, find, concat, map as lodashMap } from 'lodash';

import { APPLICATION_TYPE } from './constants/constants';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { DashboardService } from '../../services/dashboard.service';
import { DiagnosticOrderActionsService } from '@ispm/test/services/diagnostic-order-actions.service';
import { DynamicSearchPayload, IdashboardFilter, Filter as DyFilter } from '@ispm-shared/modules/common/dynamic-search';
import { DynamicSearchService } from '../../services/dynamic-search.service';
import { ExportConfigInterface } from '../../interfaces/export.config.interface';
import { Filter } from '../../../filter/classes/filter';
import { FilterActionCreatorService } from '../../../filter/services/filter-action-creator.service';
import { FilterBucketComponent } from '../../../filter/components/filter-bucket/filter-bucket.component';
import { FilterService } from '../../../filter/services/filter.service';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { IModalConfiguration } from '@ispm/shared/interfaces/action.interface';
import { ISelectedFacilityAssociation } from '@ispm-client/modules/aggregator/interfaces';
import { IuserState } from '@ispm/user/store/user.reducer';
import { LogService } from '@ispm/core/services/log.service';
import { NavigationConfigService } from '@ispm/navigation/services/navigationConfig.service';
import { PatientInfoService } from '@ispm/user/services/patientInfo.service';
import { PrefetchingService } from '@ispm/core/services/prefetching.service';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { SessionStorageService } from '@ispm/shared/services/session-storage.service';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { UserService } from '@ispm/user/services/user.service';
import { WorklistHelper } from '../../worklist-helper';
import { WorklistService } from '../../services/worklist.service';
import {FilterUtils} from "../../../filter/utils/filter-utils";
import {ActivatedRoute, Router} from "@angular/router";

@Component({
  selector: 'gen-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, OnDestroy, OnChanges, IModalConfiguration {
  @ViewChild('filterBucket', { static: false}) filterBucket: FilterBucketComponent;

  constructor(private _changeDetector: ChangeDetectorRef,
    private _dashboardService: DashboardService,
    private _formHandler: FormHandlerService,
    private authService: AuthenticationService,
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private dynamicSearchService: DynamicSearchService,
    private el: ElementRef,
    private filterAC: FilterActionCreatorService,
    private filterService: FilterService,
    private logService: LogService,
    private navigationConfigService: NavigationConfigService,
    private patientInfoService: PatientInfoService,
    private prefetchingService: PrefetchingService,
    private routerManagerService: RouterManagerService,
    private sessionStorageService: SessionStorageService,
    private userActionCreatorService: UserActionCreatorService,
    private userService: UserService,
    private worklistService: WorklistService, private router: Router, private activatedRoute: ActivatedRoute
  ) {
    const stateParamsType = this.routerManagerService.parameters;
    const stateParamsQuery = this.activatedRoute.snapshot.queryParams;
    const stateParams = {...stateParamsQuery,...stateParamsType};
    this._transactionId = stateParams.transactionId || logService.generateTransactionId();
    this.logService.info(`[CTOR] Inside dashboard component ctor`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
    userActionCreatorService.getStore()
      .pipe(
        filter(( userStore: IuserState) => userStore.reloadAdminWorkList === true)
      )
      .subscribe((users) => {
        if (users.reloadAdminWorkList) {
          userActionCreatorService.setReloadAdminWorklist(false);
          this.loadDashboardConfiguration();
          this.setStateParams();
        }
      });
    this._filterSearch
      .pipe(
        debounceTime(2000),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current))
      )
      .subscribe((value: string) => {
        this.onSearchExecute(value);
      });

    this._dashboardService.isFacilityEnabledWithoutAssociation().pipe(takeUntil(this._destroyed$.asObservable())).subscribe((data) => {
      this._isFacilityEnabled = data;
    });
  }

  public _exportConfig: Partial<ExportConfigInterface>;
  public adaptableBucketFiltersConfig: Array<any>;
  public addBtnLabel: string;
  public antiSpamMyWorklistToggle: boolean = false;
  public appliedFilterProperties: any[] = [];
  public appliedFilterQueryObj: any = -1;
  public associationItems: Array<any>;
  public bulkOperationConfig: any;
  public bulkOperationDisabled: boolean;
  public dashboardFilters: Array<IdashboardFilter>;
  public dashboardFiltersOptions: Array<{ id: string, label: string, command: () => void, active: boolean }> = [];
  public disableAddBtn: boolean = false;
  public dynamicSearchConfig: DynamicSearchPayload;
  public enableStatistics: boolean = false;
  public exportConfig: Partial<ExportConfigInterface>;
  public exportData: any;
  public filterBucketEnabled: boolean = false;
  public filterEnabled: boolean = false;
  public filterMainType: string;
  public filterType: string;
  public hideSearch: boolean = false;
  public isFilterPanelOpen;
  public isFullScreen: boolean = true;
  public loading: boolean = true;
  public patientId: string;
  public placeHolderText: string;
  public resetWorklist: boolean = false;
  public resource: string;
  public savedFiltersList: Map<string, Filter>;
  public searchText: string = '';
  public searchResource: string = '';
  public showAddBtn: boolean = false;
  public title: string;
  public worklistType: string;
  public worklistChunkSize: number;
  public worklistSearchPropagation: boolean = true;

  private readonly CURATION_DASHBOARD_COMPONENT = 'CURATION_DASHBOARD_COMPONENT';
  private readonly _transactionId: string;
  private _appliedFilter$: Subscription;
  private _destroyed$: Subject<any> = new Subject();
  private _filterSearch: Subject<string> = new Subject<string>();
  private routeDataSubscription: Subscription;
  private _isFacilityEnabled: boolean = false;
  private _newTestConfig: any;
  private _parentResource: string;
  public dashboardConfig;
  @Input() worklistConfig;
  private _worklistToggleId: string;
  private _facilityFilterInterpolated: DyFilter[];
  private _selectedFilter: any;

  private static checkIfFieldChanged(changes: SimpleChanges, fieldName: string) {
    return changes[fieldName] &&
      (!changes[fieldName].firstChange && (changes[fieldName].currentValue !== changes[fieldName].previousValue));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (DashboardComponent.checkIfFieldChanged(changes, 'worklistConfig')){
      this.dashboardConfig = this.worklistConfig;
      this.appliedFilterQueryObj = -1;
      this.loadDashboardConfiguration();
    }
  }

  ngOnInit(): void {
    //this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.logService.info(`[OnInit] Loading Dashboard Component`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
    this.appliedFilterQueryObj = -1;
    this.filterService.isWorklistFilter = true;
    this.routeDataSubscription = this.activatedRoute.data
    .pipe(filter((data) => !!data))
    .subscribe(data => {
      this.loading = true;
      this.dashboardConfig = this.worklistConfig ? this.worklistConfig : this.activatedRoute.snapshot.data['dashboardConfig'];
      this.enableSubscriptionForFacility();
      this.loadDashboardConfiguration();
    });
    this.setStateParams();
    this.logService.info(`[OnInit] Done Loading Dashboard Component`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
    this.worklistSearchPropagation = this._dashboardService.isWorklistsSearchPropagationAvailable();
  }

  ngOnDestroy(): void {
    if (this._appliedFilter$) {
      this._appliedFilter$.unsubscribe();
    }
    if(this.el) {
      this.el = null;
    }
    if(this.filterBucket) {
      this.filterBucket = null;
    }
    if (this.routeDataSubscription) {
      this.routeDataSubscription.unsubscribe();
    }
    this._dashboardService.closeDynamicFormModal();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  loadDashboardConfiguration() {
    this.logService.info(
      `[loadDashboardConfiguration] Loading Dashboard Configuration`,
      this.CURATION_DASHBOARD_COMPONENT,
      this._transactionId
    );
    this.diagnosticOrderActionsService.setDiagnosticOrder(null);
    this.searchText = this.authService.getLocalSetting('worklistSearchText', '');
    this.searchResource = this.authService.getLocalSetting('worklistSearchResource', '');
    this.loading = true;
    this.showAddBtn = false;
    this.savedFiltersList = new Map<string, Filter>();
    this.authService.setCurrentUser(this.authService.user);
    this.title = this.dashboardConfig.title;
    this._parentResource = get(this.dashboardConfig, 'parentResource', this.dashboardConfig.resource);
    this.dashboardFilters = get(this.dashboardConfig, 'dashboardFilters', []);
    if (JSON.parse(sessionStorage.getItem('isNavFromAdmin'))) {
      if (this.dashboardFilters && this.dashboardFilters[0] && this.dashboardFilters[0].defaultEnabled) {
        this.dashboardFilters[0].defaultEnabled = false;
      }
    }

    this.makeDashboardFiltersInterpolated().then();
    this.filterMainType = this._parentResource;
    this.filterEnabled = this.dashboardConfig.filterable ? this.dashboardConfig.filterable : false;
    this.filterBucketEnabled = this.dashboardConfig.filterBucketEnabled ? this.dashboardConfig.filterBucketEnabled : false;
    this.resource = this.dashboardConfig.resource;
    this.worklistType = this.dashboardConfig.type;
    this.hideSearch = this.dashboardConfig.hideSearch ? this.dashboardConfig.hideSearch : false;
    this.placeHolderText = this.dashboardConfig.searchPlaceholderText;

    if (this.dashboardConfig.hasOwnProperty('newTestConfig') && !(isEmpty(this.dashboardConfig.newTestConfig))) {
      this.showAddBtn = true;

      if (this.dashboardConfig.newTestConfig.validateAddButtonForOrg) {
        this._dashboardService.getOrganizationDataFromStore().subscribe(org => {
          this.disableAddBtn = org && !org._isFacilityEnabled && !org.isFacilityEnabled;
        });}

      this.addBtnLabel = this.dashboardConfig.newTestConfig.buttonLabel;
      this._newTestConfig = this.dashboardConfig.newTestConfig;
      this._dashboardService.dashboardConfig = this.dashboardConfig;
      this._dashboardService.newTestConfig = this._newTestConfig;
      this._formHandler.getForm(this._newTestConfig.payload.formId, this._newTestConfig.payload.forceFormFetch);
    }

    this.bulkOperationConfig = this.dashboardConfig.bulkOperations;
    this.associationItems = get(this.dashboardConfig, 'associationItems', []);
    // set of adaptable filters for buckets
    this.adaptableBucketFiltersConfig = get(this.dashboardConfig,'adaptableFilters.buckets',[]);

    // TODO
    // change  Using field directly as field is served from a observable
    if (this._isFacilityEnabled) {
      this.associationItems = this.associationItems.concat(this._dashboardService.getFacilityAssociationItem(this.resource));
    }

    this.dynamicSearchConfig = get(this.dashboardConfig, 'dynamicSearch.idQuery', { });

    if (get(this.dashboardConfig, 'dynamicSearch.clubbed')) {
      this.dynamicSearchConfig = get(this.dashboardConfig, 'dynamicSearch.dataQuery', { });
    }

    if (get(this.dashboardConfig, 'dynamicSearch.clubbed') && this._isFacilityEnabled) {
      this.dynamicSearchConfig = this._dashboardService.addFacilityToFieldsList(this.resource, this.dynamicSearchConfig);
    }

    this.exportData =  get(this.dashboardConfig, 'exportData', '');
    // TODO: temp. solution for GEN-104959 - the next line should be removed when the export functionality will be added
    this.exportData = this.exportData && this.dashboardConfig.id !== 'ADMIN_VALUESET_WORKLIST_TEMPLATE_DEFAULT';
    // TODO why do we need two identical vars exportConfig and _exportConfig?
    this.exportConfig = Object.assign({ }, {...get(this.dashboardConfig, 'exportConfiguration', { })});
    this._exportConfig = Object.assign({ }, this.exportConfig);
    this.worklistChunkSize = get(this.dashboardConfig, 'chunkSize', 60);
    this.filterType = get(this.dashboardConfig, 'filterSubType', 'worklist');
    this.filterService.filterChanged();

    if (this.filterEnabled) {
      if (!isEmpty(this._exportConfig)) {
        this.filterService.getCurrentSelectedFilterFromStore(true)
          .pipe(
            distinctUntilChanged(),
            takeUntil(this._destroyed$.asObservable()),
            filter(data => (data !== undefined))
          )
          .subscribe((data) => {
            if (((get(data, 'filter.default') === true) && (this._exportConfig.options))) {
              this.exportConfig.options = [...this._exportConfig.options.filter(item => !item.hideForDefaultFilter)];
              this._selectedFilter = data;
            }else {
              this.exportConfig.options = this._exportConfig.options;
              this._selectedFilter = data;
            }
          });
      }

      this._appliedFilter$ = this.loadFiltersFromServer()
        .pipe(
          takeUntil(this._destroyed$.asObservable())
        )
        .subscribe((queryObj) => {
          if (queryObj) {
            this.appliedFilterQueryObj = queryObj;
          }

          this._changeDetector.detectChanges();
        });
      this.filterService.getAppliedFilterPropertiesFromStore()
        .pipe(
          takeUntil(this._destroyed$.asObservable())
        )
        .subscribe(appliedFilterProps => {
          this.appliedFilterProperties = appliedFilterProps ? appliedFilterProps : [];
        });
    } else {
      this._changeDetector.detectChanges();
    }

    this.loading = false;
    this._changeDetector.markForCheck();
    this.logService.info(`[loadDashboardConfiguration] loading filter Bucket`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
    this.loadFilterBucket();
    this.logService.info(`[loadDashboardConfiguration] loading Custom Css`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
    this.loadCustomCSS();
    this.logService.info(`[loadDashboardConfiguration] Prefetching Resources for Pathways Test`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
    this.prefetchResourcesForPathwayTest();
    this.logService.info(`[loadDashboardConfiguration] Loading Dashboard Configuration Done`, this.CURATION_DASHBOARD_COMPONENT, this._transactionId);
  }

  onRowClick(worklistItem: any) {
    // TODO Below code needs to generised based on config
    if (worklistItem.WorkflowRequest) {
      this.worklistService.redirectToPatientProfileTest(worklistItem.subject, worklistItem.WorkflowRequest.id, get(worklistItem, 'WorkflowRequest.revisions_-1_id'));
    }
  }

  reloadTriggeredForDashboardFilters(event){
    if (this.dashboardFilters.length === 1) {
      this.reloadTriggered(event);
    }
  }

  reloadTriggered(event) {
    if ((event) && this.filterBucketEnabled && this.filterBucket) {
      this.filterBucket.reCalculateFilters();
    }
  }

  onSearchChange(searchStr: string) {
    // When user deletes the filter and click on enter 'onSearchClear' event is not called so triggering the filter clear here instead.
    this.authService.updateLocalSettings('worklistSearchText', searchStr);
    this.authService.updateLocalSettings('worklistSearchResource', this.worklistType);
    this.searchResource = this.worklistType;
    this._filterSearch.next(searchStr);
  }

  onSearchExecute(searchStr: string) {
    if (!searchStr) {
      searchStr = '';
    }

    this.searchText = searchStr;
    this.authService.updateLocalSettings('worklistSearchText', searchStr);
    this.authService.updateLocalSettings('worklistSearchResource', this.worklistType);
    this.searchResource = this.worklistType;
    this._changeDetector.detectChanges();
  }

  updatePanelState(event: boolean) {
    this.isFilterPanelOpen = event;
  }

  enableSubscriptionForFacility() {
    this._dashboardService.getSelectedFacilityStringAndAssociation()
      .pipe(
        distinctUntilChanged(),
        takeUntil(this._destroyed$.asObservable()),
        filter(config => config !== null)
      )
      .subscribe(async(config: ISelectedFacilityAssociation) => {
        if (config && config.active && !(isEmpty(config.selectedFacilitiesString))) {
          const facilityFilters: Array<DyFilter> = this._dashboardService.getFacilityFilters(config.association, this.dashboardConfig.resource);

          if (!isEmpty(facilityFilters)) {
            const filtersInterpolated = await  this.dynamicSearchService.interpolateDashboardLevelFilters(facilityFilters, { }).toPromise();
            this._facilityFilterInterpolated = [...filtersInterpolated];
            this.dynamicSearchService.setDashboardLevelFilters(filtersInterpolated);

            if (config.applied) {
              this.reloadTriggeredForDashboardFilters(true);

              if (this._changeDetector) {
                this.resetWorklist = true;
                this._changeDetector.markForCheck();
                this._changeDetector.detectChanges();
                this.resetWorklist = false;
              }
            }
          }
        }
      });
  }

  clearAllAppliedFilters(): void {
    this.filterService.clearAppliedFilters();
    this.clearHighlightedClass();
  }

  clearHighlightedClass() {
    this.filterBucket.clearHighlighted();
    let defaultFilter;
    let defaultCategories;
    this.filterService.getDefaultFilter(this.filterMainType, this.filterType).
    subscribe(
      res => defaultFilter = res
    );
    this.filterService.getCurrentDefaultCategories().subscribe(
      res => defaultCategories = res
    );
    const filterTypeAndSubtype = FilterUtils.buildFilterTypes(this.filterMainType, [this.filterType]);
    const currentSelectedFilterAndCategories = {
      filter: defaultFilter,
      categories: defaultCategories,
      filterTypeAndSubtype: filterTypeAndSubtype
    };
    this.filterService.setCurrentSelectedFilter(currentSelectedFilterAndCategories);
  }

  addTest():  void {
    if (this.dashboardConfig && this.dashboardConfig.type === 'patient') {
      this.patientInfoService.openPatientInfo(null).subscribe((resp) => {
        if (resp.status !== 2 && resp.data === 1) {
          this.loadDashboardConfiguration();
        }
      });
    } else {
      this._dashboardService.runConfiguration(this._newTestConfig, this.dashboardConfig);
    }
  }

  checkRolePermissionForAddTest() {
    if (this.authService.user) {
      if (this.authService.user.permissions.includes(this.filterMainType.toUpperCase() + '.CREATE')) {
        return false;
      } else {
        const role = this.authService.user.role;
        return (role.toLowerCase() === 'genomicsadmingroup')
          ? !this.hasPermissionToCreateWorklist()
          : false;
      }
    }

    return false;
  }

  setStateParams() {
    this.activatedRoute.data
      .pipe(takeUntil(this._destroyed$.asObservable()))
      .subscribe(data => {
        const stateParams = {...data, ...this.routerManagerService.parameters};
        if (stateParams) {
          if (stateParams.hasOwnProperty('isFullScreen')) {
            this.isFullScreen = stateParams.isFullScreen;
          }

          if (stateParams.hasOwnProperty('patientId')) {
            this.patientId = stateParams.patientId;

            if (this.worklistType.includes(WorklistHelper.PATIENT_DASHBOARD_TEST)) {
              // Loading patient details to the cache for patient details panel usage (in TP worklist)
              this.userService.getPatient(this.patientId, true).pipe(take(1)).subscribe();
            }
          }
        }
      });
  }

  selectDashboardLevelFilters(selected: string = '', reset: boolean = false, needFacilityFilter: boolean = false, fromClick: boolean = false) {
    const facilityFilter = [...(this._facilityFilterInterpolated ?? [])];
    if (reset) {
      if (needFacilityFilter && this._isFacilityEnabled) {
        this.dynamicSearchService.setDashboardLevelFilters([...facilityFilter], false);
      } else {
        this.dynamicSearchService.setDashboardLevelFilters( [], true);
      }
    } else {
      const defaultFilter = find(this.dashboardFilters, (obj: IdashboardFilter) => obj.id === selected);
      const getFilters = get(defaultFilter, 'filters', []);

      this.dynamicSearchService.setDashboardLevelFilters(
        (needFacilityFilter && this._isFacilityEnabled)
          ? [...getFilters, ...facilityFilter]
          : [...getFilters]
      );
    }

    this.resetWorklist = true;
    this.reloadTriggeredForDashboardFilters(fromClick);

    // without set time out reset is unset immediately
    setTimeout(() => {
      this.resetWorklist = false;
    }, 1000);
  }

  toggleSelection(event,fromClick = false) {
    this.antiSpamMyWorklistToggle = true;
    setTimeout(() => {
      this.antiSpamMyWorklistToggle = false;
    }, 2000);
    localStorage.setItem(this._worklistToggleId, event);

    if (event) {
      this.selectDashboardLevelFilters(this.dashboardFiltersOptions[0].id, false, true, fromClick);
    } else {
      this.selectDashboardLevelFilters('', true, true, fromClick);
    }
  }

  showPatientHeader() {
    return this.worklistType.includes(WorklistHelper.PATIENT_DASHBOARD_TEST);
  }

  setBulkOperationDisabledStatus(value) {
    this.bulkOperationDisabled = value;
  }

  private loadCustomCSS() {
    const elements = this.el.nativeElement.children;

    for (let i = 0; i < elements.length; i++) {
      if (elements[i].id === 'customPageCSS') {
        this.el.nativeElement.removeChild(elements[i]);
        break;
      }
    }

    if (this.dashboardConfig && this.dashboardConfig.customPageCSS) {
      let style: HTMLStyleElement;
      style = document.createElement('style');
      style.id = 'customPageCSS';
      this.el.nativeElement.appendChild(style);
      const cssArray = this.dashboardConfig.customPageCSS.split(',');

      for (const rule of cssArray) {
        if (style) {
          (style.sheet as CSSStyleSheet).insertRule(rule, 0);
        }
      }
    }
  }

  private prefetchResourcesForPathwayTest(): void {
    this.navigationConfigService.getCurrentApp().subscribe((currentApp) => {
      const isPatientTestList = this.worklistType.includes(WorklistHelper.PATIENT_DASHBOARD_TEST);

      if (get(currentApp, 'applicationType', '') === APPLICATION_TYPE.PATHWAY_NAVIGATOR && isPatientTestList) {
        window.setTimeout(() => {
          this.prefetchingService.prefetchOrganization();
          this.prefetchingService.prefetchTestCollections();
          this.prefetchingService.prefetchResourcesForTest();
        }, 500);
      }
    });
  }

  private loadFiltersFromServer(): Observable<any> {
    if (this.filterService.areFiltersInCache()) {
      this.filterService.restoreState();
      return this.filterService.getCurrentSelectedFilterQueryFromStore();
    } else {
      return this.filterService.getAllApplied()
        .pipe(
          take(1),
          tap(allAppliedFilterTypes => {
            const key = this.filterMainType + '_' + this.filterType;
            if (!allAppliedFilterTypes.has(key)) {
              const connectable = this.filterService.loadFiltersList(this.filterMainType, this.filterType, this.filterType, { }, false)
                .pipe(
                  mergeMap(() => this.filterService.getCurrentSelectedFilterQueryFromStore()), publish()
                ) as ConnectableObservable<any>;
              connectable.connect();
            } else {
              this.filterService.setFilterType(this.filterMainType, this.filterType);
            }
          }),
          flatMap(() => {
            return this.filterService.getCurrentSelectedFilterQueryFromStore();
          })
        );
    }
  }

  private loadFilterBucket(): void {
    const key = this.filterMainType + '_' + this.filterType;
    this.filterAC.getCurrentAppliedFiltersContinuously(key)
      .pipe(
        map(currentFilters => {
          if (currentFilters && currentFilters.data) {
            this.savedFiltersList = new Map<string, Filter>();
            setTimeout(() => {
              if (key === currentFilters.key) {
                this.savedFiltersList = currentFilters.data;
                if (this._changeDetector && !((<ViewRef> this._changeDetector).destroyed)) {
                  this._changeDetector.detectChanges();
                }
              }
            }, 300);
          }
        })).subscribe();
  }

  private async makeDashboardFiltersInterpolated() {
    let defaultFilters, allFilters: Array<DyFilter> = [];

    if (isEmpty(this.dashboardFilters)) {
      this.dynamicSearchService.setDashboardLevelFilters([], true);
    }

    this.dashboardFilters.forEach((singleDash) => {
      singleDash.filters = lodashMap(singleDash.filters, function(oneDyFilter: DyFilter) {
        oneDyFilter.parentId = singleDash.id;
        return oneDyFilter;
      });
      allFilters = concat(allFilters, singleDash.filters);
    });

    const filtersInterpolated = await this.dynamicSearchService.interpolateDashboardLevelFilters(allFilters, { }).toPromise();
    this.dashboardFilters.forEach((oneFilter: IdashboardFilter) => {
      oneFilter.filters = filtersInterpolated.filter((entry: DyFilter) => entry.parentId === oneFilter.id);
      if (oneFilter.defaultEnabled) {
        defaultFilters = oneFilter.filters;
        this.dynamicSearchService.setDashboardLevelFilters(defaultFilters);
      }
    });
    this.setFilterOptions(); // This will be enabled when multi facility filters come

    try {
      this._worklistToggleId = this.worklistType + '.' + 'worklist.toggle';

      let noCacheToggleStatus = true;
      if(this.dashboardFilters.length == 1 && this.dashboardFilters[0].defaultEnabled == false) {
        noCacheToggleStatus = this.dashboardFilters[0].defaultEnabled;
      }
      const toggleStatus: boolean = localStorage.getItem(this._worklistToggleId) ? JSON.parse((localStorage.getItem(this._worklistToggleId))) : noCacheToggleStatus;

      if (toggleStatus != null) {
        this.dashboardFiltersOptions[0].active = toggleStatus;
        this.toggleSelection(toggleStatus);
      }
    } catch (error) {
      console.error('Local storage worklist toggle value parser error:' + JSON.stringify(error));
      return error;
    }
  }

  private setFilterOptions() {
    let anythingDefault: boolean = false;
    this.dashboardFilters.forEach((filter: IdashboardFilter) => {
      if (filter.defaultEnabled) {
        anythingDefault = filter.defaultEnabled;
      }

      this.dashboardFiltersOptions.push({
        id: filter.id,
        label: filter.label,
        command: () => {
          this.selectDashboardLevelFilters(filter.id);
        },
        active: filter.defaultEnabled
      });
    });
    this.dashboardFiltersOptions.push({
      id: 'none',
      label: 'None',
      command: () => {
        this.selectDashboardLevelFilters('', true);
      },
      active: !anythingDefault
    });
  }

  private hasPermissionToCreateWorklist() {
    return (this.authService.user.permissions.includes('ADMIN.ORGANIZATION.WORKLIST')
      && this.authService.user.permissions.includes('ADMIN.ORGANIZATION.WORKLIST.CREATE'));
  }
}
